import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { watchPath, saveRecord, deleteRecord, listMembers } from '../../services/crmService';
import { useVisibleContacts, useOwned, useTeam, canViewAll, ownedFilterMatch } from './useCrm';
import { ownerOf } from '../../services/recordAccess';
import { ui } from './crmUi';
import { useNavigate } from 'react-router-dom';
import ListEmailBlast from './ListEmailBlast';

const EMPTY = { name: '', kind: 'static', contactIds: [], tags: [], matchAll: false };

export default function ListsPage() {
  const { user, userRole } = useAuth();
  const navigate = useNavigate();
  const [lists, setLists] = useState([]);
  const contacts = useVisibleContacts();
  const [l, setL] = useState(EMPTY);
  const [status, setStatus] = useState('');

  const own_Lists = useOwned('crm/lists');
  useEffect(() => setLists(own_Lists), [own_Lists]);
  const allTags = useMemo(() => [...new Set(contacts.flatMap((c) => c.tags || []))].sort(), [contacts]);
  const team = useTeam();
  const viewAll = canViewAll(userRole);
  // Filters for admins and the Marketing Manager: which lists (by who made them) and which members (by assigned agent / Team or Agent).
  const [listAgent, setListAgent] = useState('');
  const [listOwned, setListOwned] = useState('');
  const [memAgent, setMemAgent] = useState('');
  const [memOwned, setMemOwned] = useState('');
  const shownLists = lists.filter((x) => (!listAgent || x.createdBy === listAgent) && ownedFilterMatch(listOwned, x, team, x.createdBy));
  const allMembers = listMembers(l, contacts);
  const members = viewAll ? allMembers.filter((m) => { const c = contacts.find((k) => k.id === m.id) || m; return (!memAgent || ownerOf(c) === memAgent || c.assignedToId === memAgent) && ownedFilterMatch(memOwned, c, team, ownerOf(c)); }) : allMembers;
  const ids = l.contactIds || [];
  const toggleIn = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const save = async () => {
    if (!l.name.trim()) { setStatus('Give the list a name.'); return; }
    const { id, createdAt, updatedAt, createdBy, ...data } = l;
    try { const newId = await saveRecord('crm/lists', id, data, user?.uid); setL({ ...l, id: newId }); setStatus('Saved.'); }
    catch (err) { console.error('Could not save list', err); setStatus('Could not save. Please try again.'); }
  };

  return (
    <div style={ui.page}>
      <h1 style={ui.h1}>Lists</h1>
      <p style={ui.sub}>Static lists you pick by hand, or smart lists that fill themselves by tag. Use them for campaigns and the Power Dialer.</p>
      <div style={{ ...ui.row, marginBottom: 14 }}>
        <select style={{ ...ui.input, width: '100%', maxWidth: 260 }} value={l.id || ''} onChange={(e) => { const x = lists.find((k) => k.id === e.target.value); setL(x ? { ...EMPTY, ...x } : EMPTY); setStatus(''); }}>
          <option value="">New list</option>
          {shownLists.map((x) => <option key={x.id} value={x.id}>{x.name} ({x.kind === 'smart' ? 'smart' : 'static'})</option>)}
        </select>
        {l.id && <button style={ui.ghost} onClick={async () => { if (window.confirm('Delete this list? Contacts are not deleted.')) { await deleteRecord('crm/lists', l.id); setL(EMPTY); } }}>Delete</button>}
      </div>
      {viewAll && (
        <div style={{ ...ui.row, marginBottom: 10 }}>
          <select style={{ ...ui.input, width: 'auto' }} value={listAgent} onChange={(e) => setListAgent(e.target.value)} aria-label="Lists made by"><option value="">Lists by any agent</option>{team.map((t) => <option key={t.uid} value={t.uid}>{t.name}</option>)}</select>
          <select style={{ ...ui.input, width: 'auto' }} value={listOwned} onChange={(e) => setListOwned(e.target.value)} aria-label="List owned by"><option value="">Team or Agent lists</option><option value="team">Team lists</option><option value="agent">Agent lists</option></select>
          <select style={{ ...ui.input, width: 'auto' }} value={memAgent} onChange={(e) => setMemAgent(e.target.value)} aria-label="Members assigned to"><option value="">Members: any assigned agent</option>{team.map((t) => <option key={t.uid} value={t.uid}>{t.name}</option>)}</select>
          <select style={{ ...ui.input, width: 'auto' }} value={memOwned} onChange={(e) => setMemOwned(e.target.value)} aria-label="Members owned by"><option value="">Members: Team or Agent</option><option value="team">Members owned by Team</option><option value="agent">Members owned by Agent</option></select>
        </div>
      )}
      <div style={ui.card}>
        <label style={ui.label}>List name</label>
        <input style={ui.input} value={l.name} onChange={(e) => setL({ ...l, name: e.target.value })} />
        <label style={ui.label}>Type</label>
        <div style={ui.row}>
          {['static', 'smart'].map((k) => (
            <label key={k} style={{ fontSize: 13, display: 'flex', gap: 6 }}><input type="radio" checked={l.kind === k} onChange={() => setL({ ...l, kind: k })} />{k === 'static' ? 'Static (pick contacts)' : 'Smart (by tag)'}</label>
          ))}
        </div>
        {l.kind === 'smart' && <>
          <label style={ui.label}>Tags</label>
          {allTags.length === 0 && <p style={ui.sub}>No tags yet. Add tags to contacts first.</p>}
          <div>{allTags.map((t) => (
            <button key={t} onClick={() => setL({ ...l, tags: toggleIn(l.tags || [], t) })} style={{ ...ui.tag, border: (l.tags || []).includes(t) ? '2px solid #D4AF37' : '2px solid transparent', cursor: 'pointer' }}>{t}</button>
          ))}</div>
          <label style={{ fontSize: 13, display: 'flex', gap: 6, marginTop: 8 }}><input type="checkbox" checked={!!l.matchAll} onChange={(e) => setL({ ...l, matchAll: e.target.checked })} />Contact must have all selected tags</label>
        </>}
      </div>
      {l.kind === 'static' && (
        <div style={ui.card}>
          <strong>Adding contacts</strong>
          <p style={ui.sub}>Build lists from the Contacts page: filter or search, press Create list, tick the people you want, and name the list. Remove someone below.</p>
          <button style={ui.ghost} onClick={() => navigate('/leads/contacts?createList=1')}>Go to Contacts to create a list</button>
        </div>
      )}
      <div style={ui.card}>
        <strong>In this list ({members.length}{members.length !== allMembers.length ? ` of ${allMembers.length}, filtered` : ''})</strong>
        <div style={{ marginTop: 6 }}>{members.map((m) => <span key={m.id} style={ui.tag}>{m.name}{m.dnc ? ' (DNC)' : ''}{l.kind === 'static' && <button aria-label={`Remove ${m.name}`} onClick={() => setL({ ...l, contactIds: ids.filter((x) => x !== m.id) })} style={{ border: 'none', background: 'none', cursor: 'pointer', marginLeft: 4 }}>x</button>}</span>)}</div>
        {l.id && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start', marginTop: 12 }}>
            <button style={ui.btn} onClick={() => navigate(`/leads/dialer?list=${l.id}`)} title="Opens the Power Dialer with this list. Archived, DNC, opted-out and paused contacts and anyone without a phone are skipped.">Add to Power Dialer</button>
            <ListEmailBlast list={l} members={members} uid={user?.uid} isAdmin={userRole === 'Admin'} />
          </div>
        )}
      </div>
      <div style={ui.row}><button style={ui.btn} onClick={save}>Save list</button><span style={{ fontSize: 13 }}>{status}</span></div>
    </div>
  );
}
